import React from "react";
import { colors } from "./authStyles";

const BENEFITS = [
  "Secure checkout with Stripe",
  "Order status and history in one place",
  "Pick up in Scarborough",
];

const CheckIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke={colors.cyan}
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

// Split layout for the account pages: a black story panel on the left and the
// form on the right. The panel runs full height from the top of the page
// (behind the fixed header) and stays put while the form scrolls, so it can
// later hold a photo. Stacks into a short hero above the form on small screens.
const AuthLayout = ({
  eyebrow = "YOUR 900 ACCOUNT",
  headline = "Order online.\nPick up at the lanes.",
  body,
  children,
}) => {
  return (
    <>
      <style>
        {`
          .auth-page {
            --header-height: 72px;
            /* Matches the header: 1200px container with 1.5rem padding. */
            --edge: max(1.5rem, calc((100% - 1200px) / 2 + 1.5rem));
            display: flex;
            align-items: flex-start;
            min-height: 100vh;
            background-color: ${colors.page};
          }
          .auth-story {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            gap: 3rem;
            position: sticky;
            top: 0;
            height: 100vh;
            /* Half the screen, never narrower than 480px. The text lines up with
               the header logo (1200px container), so on wide screens the panel
               has to grow too or the text gets squeezed. */
            width: max(480px, 50%);
            flex-shrink: 0;
            padding: calc(var(--header-height) + 6rem) 4rem 4.5rem var(--edge);
            background-color: ${colors.black};
            overflow: hidden;
          }
          .auth-headline {
            font-size: clamp(2.5rem, 3.4vw, 3.25rem);
            line-height: 1.08;
            text-wrap: balance;
            font-weight: 900;
            letter-spacing: -0.025em;
            color: white;
            margin: 0;
            white-space: pre-line;
          }
          .auth-body {
            font-size: 1.125rem;
            line-height: 1.55;
            color: #c7c7c7;
            max-width: 440px;
            margin: 0;
          }
          .auth-form-side {
            display: flex;
            flex: 1;
            align-items: center;
            justify-content: center;
            min-height: 100vh;
            /* Same padding both sides so the form sits centered in the panel. */
            padding: calc(var(--header-height) + 3.5rem) 4rem 3.5rem;
          }
          @media (max-height: 760px) {
            .auth-story {
              padding-top: calc(var(--header-height) + 3rem);
              padding-bottom: 2.5rem;
            }
          }
          /* Short windows: the panel can't scroll, so drop the extras. */
          @media (max-height: 640px) {
            .auth-headline {
              font-size: 2.5rem;
            }
            .auth-benefits,
            .auth-ball,
            .auth-ball-glow {
              display: none !important;
            }
          }
          .auth-ball-glow {
            position: absolute;
            right: -300px;
            bottom: -330px;
            width: 640px;
            height: 640px;
            border-radius: 50%;
            background: radial-gradient(circle, rgba(8,145,178,0.55) 0%, rgba(8,145,178,0) 65%);
            pointer-events: none;
          }
          .auth-ball {
            position: absolute;
            right: -170px;
            bottom: -190px;
            width: 380px;
            height: 380px;
            border-radius: 50%;
            background: radial-gradient(circle farthest-corner at 35% 30%, #595959 0%, #1f1f1f 70%);
            pointer-events: none;
          }
          .auth-ball span {
            position: absolute;
            border-radius: 50%;
            background: #050505;
          }
          /* Below laptop width, stack: a short banner above the form. */
          @media (max-width: 1024px) {
            .auth-page {
              flex-direction: column;
              align-items: stretch;
            }
            .auth-story {
              position: relative;
              height: auto;
              width: 100%;
              /* Line the banner text up with the 400px form below it. */
              padding: calc(var(--header-height) + 1.75rem) max(1rem, calc((100% - 400px) / 2)) 2rem;
            }
            .auth-headline {
              font-size: 2.125rem;
              max-width: 260px;
            }
            .auth-body,
            .auth-benefits {
              display: none !important;
            }
            .auth-ball {
              right: -60px;
              bottom: auto;
              top: calc(var(--header-height) + 10px);
              width: 170px;
              height: 170px;
            }
            .auth-ball-glow {
              display: none;
            }
            .auth-form-side {
              align-items: flex-start;
              justify-content: center;
              min-height: 0;
              padding: 2rem 1rem 3rem;
            }
          }
          /* The header switches to its compact mobile size here. */
          @media (max-width: 768px) {
            .auth-page {
              --header-height: 59px;
            }
          }
        `}
      </style>

      <div className="auth-page">
        <section className="auth-story">
          <div className="auth-ball-glow" />
          <div className="auth-ball">
            <span style={{ left: "31%", top: "25%", width: "8%", height: "8%" }} />
            <span style={{ left: "44%", top: "22%", width: "8%", height: "8%" }} />
            <span style={{ left: "39.5%", top: "42%", width: "9.5%", height: "9.5%" }} />
          </div>

          <div
            style={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              gap: "1.25rem",
            }}
          >
            <span
              style={{
                fontSize: "0.8rem",
                fontWeight: 600,
                letterSpacing: "0.14em",
                color: colors.cyan,
              }}
            >
              {eyebrow}
            </span>
            <h2 className="auth-headline">{headline}</h2>
            {body && <p className="auth-body">{body}</p>}
          </div>

          <ul
            className="auth-benefits"
            style={{
              position: "relative",
              listStyle: "none",
              margin: 0,
              padding: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.9rem",
            }}
          >
            {BENEFITS.map((benefit) => (
              <li
                key={benefit}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "0.75rem",
                  color: "white",
                  fontSize: "1rem",
                }}
              >
                <CheckIcon />
                {benefit}
              </li>
            ))}
          </ul>
        </section>

        <section className="auth-form-side">{children}</section>
      </div>
    </>
  );
};

export default AuthLayout;
